import { logger } from 'packages/logger';
import { STORAGE_KEYS } from 'tradera-constants/storage';
import StorageService from '../../../app/data/storage-service';
import { axiosConfigs } from 'tradera-utils/api';
import { searchSuggestionsClient } from 'tradera-utils/http';

const previousSearchService = new StorageService(STORAGE_KEYS.SEARCH_RECENT);

export const getSuggestions = async ({
    searchTerm = '',
    limitPrevious = 7,
    enablePrevious = false,
    enableSuggestions = false
}) => {
    const generateSuggestionId = (categoryId, term) =>
        categoryId ? categoryId + '-' + term : term;

    let previousSearchResults = [],
        suggestedResults = {
            searches: [],
            shops: [],
            charities: [],
            campaigns: []
        };

    // ------------------------------
    // get suggestion data
    if (enablePrevious) {
        previousSearchResults = getPreviousSearches(searchTerm, limitPrevious);
    }

    if (enableSuggestions) {
        // eslint-disable-next-line better-mutation/no-mutation
        suggestedResults = await getSuggestionsFromBackend(searchTerm);
    }

    // ------------------------------
    // merge lists
    const withDuplicates = [].concat(
        previousSearchResults.map((item, index) => ({
            key: `previous_search-${index}`,
            type: 'previous',
            text: item.key,
            id: generateSuggestionId(null, index)
        })),
        suggestedResults.searches.map((item, index) => ({
            key: `suggested_search-${index}`,
            type: 'suggested-item',
            categories: item.categories,
            item,
            text: item.text,
            id: generateSuggestionId(
                item.category ? item.category.id : null,
                item.text
            )
        }))
    );
    const uniqueMap = new Map();
    withDuplicates.forEach(item => {
        if (uniqueMap.has(item.id)) return;
        uniqueMap.set(item.id, item);
    });
    const uniqueResults = Array.from(uniqueMap.values());

    const shopResults = suggestedResults.shops.map((item, key) => ({
        key: `suggested_shop-${key}`,
        type: 'suggested-shop',
        item,
        memberId: item.memberId,
        name: item.name,
        urlName: item.urlName,
        inSPA: false, // Turned off until profile pages are SPAified
        id: key
    }));

    const suggestedCharitiesResults = suggestedResults.charities || [];
    const charitiesResults = suggestedCharitiesResults.map((item, key) => ({
        key: `suggested_charity-${key}`,
        type: 'suggested-charity',
        item,
        name: item.name,
        slug: item.slug,
        inSPA: false, // Turned off until profile pages are SPAified
        id: key
    }));

    const suggestedCampaignResults = suggestedResults.campaigns || [];
    const campaignResults = suggestedCampaignResults.map((item, key) => ({
        key: `suggested_campaign-${key}`,
        type: 'suggested-campaign',
        item,
        capaignId: item.campaignId,
        name: item.name,
        urlName: item.urlName,
        inSPA: false, // Turned off until profile pages are SPAified
        id: key
    }));

    const allSuggestions = [
        ...campaignResults,
        ...uniqueResults,
        ...shopResults,
        ...charitiesResults,
        {
            key: 'advanced-search',
            type: 'link-advanced-search'
        },
        {
            key: 'seller-search',
            type: 'link-seller-search'
        }
    ];
    return allSuggestions;
};

// --------------------------------------------
// backend suggestions
// --------------------------------------------

const normalizeSearches = (searches = []) => {
    return searches.reduce((acc, search) => {
        if (search.categories) {
            search.categories.map(category => {
                acc.push({
                    text: search.text,
                    category: category
                });
            });
        } else {
            acc.push({
                text: search.text,
                category: null
            });
        }
        return acc;
    }, []);
};

const getSuggestionsFromBackend = async searchTerm => {
    try {
        const response = await searchSuggestionsClient.get('', {
            ...axiosConfigs.notAuthenticated,
            cancelTokenId: 'getSuggestionsFromBackend',
            params: {
                q: searchTerm
            }
        });

        const {
            campaigns = [],
            searches,
            shops = [],
            charities = []
        } = response.data;
        return {
            campaigns,
            searches: normalizeSearches(searches) || [],
            shops,
            charities
        };
    } catch (error) {
        if (error.message !== 'CANCELLED') {
            logger(error);
        }
        return { searches: [], shops: [] };
    }
};

// --------------------------------------------
// previous searches
// --------------------------------------------

// function needed by state
const getPreviousSearches = (searchTerm, limit = 7) => {
    const previousResults =
        previousSearchService.fetch().orderByTimestamp() || [];
    let output =
        searchTerm.length === 0
            ? previousResults
            : previousResults.filter(item => item.key.includes(searchTerm));
    return output.splice(0, limit);
};

export const addRecentSearch = searchTerm => {
    if (!searchTerm) {
        return;
    }
    const prettySearchTerm = searchTerm
        .toString()
        .replace(/</g, ' ')
        .replace(/>/g, ' ')
        .trim();
    if (prettySearchTerm.length > 2) {
        const recentCount =
            (previousSearchService.get(prettySearchTerm) || 0) + 1;
        previousSearchService.set(prettySearchTerm, recentCount);
    }
};

export const removeRecentSearch = searchTerm => {
    if (!searchTerm || searchTerm.length < 3) {
        return;
    }
    previousSearchService.remove(searchTerm);
};
